/* Importing the necessary libraries and files */
import { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { Form, FormikProvider, useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

/* Importing the components from the material-ui library. */
import { Breadcrumbs, Button, Card, CardContent, Container, Grid, Link, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

/* Importing the functions from the Redux slices. */
import { getQuestionnaire } from '../../features/questionnaire/questionnaireSlice';
import {
  getQuestionnaireResponse,
  postQuestionnaireResponse,
  putQuestionnaireResponse,
} from '../../features/questionnaireResponse/questionnaireResponseSlice';

/* Importing components. */
import RenderQuestionnaireForm from './RenderQuestionnaireForm';
import AutoCompletePatient from '../../components/AutoCompletePatient';
import Page from '../../components/Page';

/* Importing functions from the utils folder. */
import fetchAndHandleData from '../../utils/fetchAndHandleData';
import handleFormResponse from '../../utils/handleFormResponse';
import mapQuestionnaireResponse from '../../utils/QuestionnaireResponse/mapQuestionnaireResponse';

/* Defining the propTypes for the QuestionnaireResponseAdd component. */
QuestionnaireResponseAdd.propTypes = {
  isEdit: PropTypes.bool,
};

/**
 * Renders a page for adding or editing a questionnaire response.
 * @param {Object} props - The component props.
 * @param {boolean} props.isEdit - Indicates whether the page is for editing an existing questionnaire response.
 * @returns {JSX.Element} - The rendered component.
 */
export default function QuestionnaireResponseAdd(props) {
  const { isEdit } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  /* Using the useSelector hook to get the Patients array from the Redux store. */
  const { Questionnaires } = useSelector((state) => state.questionnaire);
  const { QuestionnaireResponses } = useSelector((state) => state.questionnaireResponse);
  const pageQuestionnaire = Questionnaires.find((el) => el.id === params.id);
  const pageQuestionnaireResponse = QuestionnaireResponses.find((el) => el.id === params.responseID);

  const [title, setTitle] = useState('Add Questionnaire Reponse');

  useEffect(() => {
    if (!pageQuestionnaire) return;
    setTitle(
      isEdit
        ? `Edit Questionnaire- ${pageQuestionnaire.title}`
        : `Add Questionnaire Response - ${pageQuestionnaire.title}`
    );
  }, [isEdit, pageQuestionnaire]);

  const extractItemLinks = (items, values) => {
    // Helper function to create answer object based on type
    const createAnswer = (type, value) => {
      switch (type) {
        case 'string':
          return { valueString: value };
        case 'boolean':
          return { valueBoolean: value };
        case 'date':
          return { valueDate: value };
        case 'integer':
          return { valueInteger: value };
        case 'decimal':
          return { valueDecimal: value };
        case 'choice':
          return { valueCoding: { code: value } };
        case 'attachment':
          return { valueAttachment: value };
        default:
          return {};
      }
    };

    return items.map((item) => {
      const itemValue = values[item.linkId];

      const itemContent = {
        linkId: item.linkId,
        text: item.text,
        ...(item.type && { type: item.type }),
        ...(itemValue !== undefined && {
          answer: (Array.isArray(itemValue) ? itemValue : [itemValue]).map((value) => createAnswer(item.type, value)),
        }),
        ...(item.item &&
          Array.isArray(item.item) && {
            item: extractItemLinks(item.item, values),
          }),
      };

      return itemContent;
    });
  };

  const QuestionnaireResponseSchema = Yup.object().shape({
    subject: Yup.string().required('Subject required'),
  });

  /* Using the useFormik hook to create a formik object. */
  const formik = useFormik({
    initialValues: {
      subject: '',
    },
    validationSchema: QuestionnaireResponseSchema,
    onSubmit: (values) => {
      const questionnaireItem = pageQuestionnaire.item;

      const questionnaireResponseData = {
        resourceType: 'QuestionnaireResponse',
        subject: {
          reference: `Patient/${values.subject}`,
          type: 'Patient',
        },
        questionnaire: pageQuestionnaire.url,
        item: extractItemLinks(questionnaireItem, values),
      };

      console.log(questionnaireResponseData);

      if (isEdit) {
        submitPutQuestionnaireResponse(questionnaireResponseData);
      } else {
        submitPostQuestionnaireResponse(questionnaireResponseData);
      }
    },
  });

  const { handleSubmit, isSubmitting, setFieldValue, values, touched, errors } = formik;

  const submitPutQuestionnaireResponse = async (values) => {
    const data = {
      questionnaireResponseID: params.responseID,
      data: { ...values, id: params.responseID },
    };
    const resultAction = await dispatch(putQuestionnaireResponse(data));
    await handleFormResponse({
      resultAction,
      successTitle: 'Questionnaire Response updated!',
      navigatePath: `/questionnaireresponse/details/${params.id}/:id`,
      navigate,
    });
  };

  const submitPostQuestionnaireResponse = async (values) => {
    const resultAction = await dispatch(postQuestionnaireResponse(values));
    await handleFormResponse({
      resultAction,
      successTitle: 'Questionnaire Response added!',
      navigatePath: `/questionnaireresponse/details/${params.id}/:id`,
      navigate,
    });
  };

  const setQuestionnaireForm = useCallback(
    (data) => {
      Object.keys(data).forEach((key) => {
        setFieldValue(key, data[key]);
      });
    },
    [setFieldValue]
  );

  useEffect(() => {
    if (!pageQuestionnaire) return;
    if (!pageQuestionnaireResponse) return;

    const mappedAnswers = mapQuestionnaireResponse(pageQuestionnaire, pageQuestionnaireResponse);
    mappedAnswers.subject = pageQuestionnaireResponse.subject.reference.split('/')[1];
    setQuestionnaireForm(mappedAnswers);
  }, [pageQuestionnaire, pageQuestionnaireResponse, setQuestionnaireForm]);

  const initializeData = useCallback(async () => {
    await fetchAndHandleData({
      dispatch,
      navigate,
      fetchAction: getQuestionnaire,
      id: params.id,
      errorMessage: 'No Questionnaire record found',
      navigateOnFailure: true,
      navigateTo: '/questionnaire/list',
    });

    if (isEdit) {
      await fetchAndHandleData({
        dispatch,
        navigate,
        fetchAction: getQuestionnaireResponse,
        id: params.responseID,
        errorMessage: 'No Questionnaire Response record found',
        navigateOnFailure: true,
        navigateTo: `/questionnaireresponse/list/${params.id}`,
      });
    }
  }, [dispatch, isEdit, navigate, params.id, params.responseID]);

  useEffect(() => {
    initializeData();
  }, [initializeData]);

  /* Rendering the header of the page. */
  const renderHeader = () => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
      <Stack direction="column" justifyContent="space-between">
        <Typography variant="h4" gutterBottom>
          {title}
        </Typography>

        <Breadcrumbs aria-label="breadcrumb" mr={4}>
          <Link underline="hover" color="inherit" component={RouterLink} to="/questionnaire/list">
            Questionnaire
          </Link>
          <Link
            underline="hover"
            color="inherit"
            component={RouterLink}
            to={`/questionnaireresponse/list/${params.id}`}
          >
            Questionnaire Response
          </Link>
          {isEdit && (
            <Link
              underline="hover"
              color="inherit"
              component={RouterLink}
              to={`/questionnaireresponse/details/${params.id}/${pageQuestionnaireResponse?.id}`}
            >
              {pageQuestionnaireResponse?.id}
            </Link>
          )}
          <Typography color="text.primary">{isEdit ? 'Edit Response' : 'Add Response'} </Typography>
        </Breadcrumbs>
      </Stack>
    </Stack>
  );

  return (
    <Page title={title}>
      <Container maxWidth="none">
        {renderHeader()}
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Grid container>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Grid container item xs={12} columnSpacing={4} rowSpacing={3}>
                      <Grid item xs={12}>
                        <Typography variant="h4">Subject</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <AutoCompletePatient
                          setFieldValue={setFieldValue}
                          fieldName={'subject'}
                          values={values}
                          touched={touched}
                          errors={errors}
                          patientID={isEdit ? values.subject : null}
                          label={'Subject*'}
                        />
                      </Grid>
                      <RenderQuestionnaireForm
                        item={pageQuestionnaire?.item}
                        setFieldValue={setFieldValue}
                        values={values}
                      />
                    </Grid>

                    <Grid container sx={{ pt: 3 }}>
                      <Grid item xs={12} md={8}>
                        <Typography variant="caption" sx={{ pt: 1 }}>
                          * Required Fields
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <Button
                            fullWidth
                            size="large"
                            variant="contained"
                            component={RouterLink}
                            to={
                              isEdit
                                ? `/questionnaireresponse/details/${params.id}/${pageQuestionnaireResponse?.id}`
                                : '/questionnaire/list'
                            }
                            color="error"
                          >
                            Cancel
                          </Button>
                          <LoadingButton
                            fullWidth
                            size="large"
                            type="submit"
                            variant="contained"
                            loading={isSubmitting}
                          >
                            {isEdit ? 'Update' : 'Add'}
                          </LoadingButton>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </Container>
    </Page>
  );
}
